import { useMutation } from '@tanstack/react-query'
import Checkbox from 'components/Checkbox/checkbox'
import { useDataTableContext } from 'components/DataTable/DataTableContext'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { cn } from 'components/UI/cn'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import { EmailThread } from 'models/emailIntegration'
import React, { useCallback, useEffect, useRef } from 'react'
import {
  PiArrowCounterClockwise,
  PiCaretDown,
  PiCaretLeft,
  PiCaretRight,
  PiPath,
  PiToolbox,
  PiTrash,
} from 'react-icons/pi'
import { toast } from 'react-toastify'
import apiService from 'services/api'
import { getDropdownZIndex } from 'styles/z-index'
import { DealSearchSubContent } from './DealSearchSubContent'

interface InboxActionBarProps {
  contactCompanyId: number
  page: number
  pageSize: number
  total: number
  threads: EmailThread[]
  isRefreshing?: boolean
  onPageChange: (page: number) => void
  onRefresh: () => void
}

/** Get selected thread IDs from DataTable selection state. */
function getSelectedThreadIds(
  threads: EmailThread[],
  rowSelection: Record<string, boolean>,
  isAllRowsSelected: boolean
): number[] {
  if (isAllRowsSelected) {
    return threads.map((t) => t.id)
  }
  return Object.entries(rowSelection)
    .filter(([, selected]) => selected)
    .map(([idx]) => threads[Number(idx)]?.id)
    .filter(Boolean)
}

/** Toolbar with select checkbox, refresh, actions dropdown, and pagination. */
export function InboxActionBar({
  contactCompanyId,
  page,
  pageSize,
  total,
  threads,
  isRefreshing,
  onPageChange,
  onRefresh,
}: InboxActionBarProps) {
  const api = apiService()

  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )

  const {
    state: {
      isAllRowsSelected,
      totalSelectedRows,
      totalRowsInPage,
      rowSelection,
    },
    methods: { setRowSelection, setIsAllRowsSelected },
  } = useDataTableContext()

  const checkboxRef = useRef<HTMLInputElement>(null!)

  // Sync indeterminate state
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate =
        !isAllRowsSelected && totalSelectedRows > 0
    }
  }, [totalSelectedRows, isAllRowsSelected])

  const selectAll = useCallback(() => {
    setIsAllRowsSelected(true)
    // Select all rows on current page
    const selection: Record<string, boolean> = {}
    threads.forEach((_, i) => {
      selection[i] = true
    })
    setRowSelection(selection)
  }, [threads, setIsAllRowsSelected, setRowSelection])

  const selectNone = useCallback(() => {
    setIsAllRowsSelected(false)
    setRowSelection({})
  }, [setIsAllRowsSelected, setRowSelection])

  const selectAllOnPage = useCallback(() => {
    setIsAllRowsSelected(false)
    const selection: Record<string, boolean> = {}
    threads.forEach((_, i) => {
      selection[i] = true
    })
    setRowSelection(selection)
  }, [threads, setIsAllRowsSelected, setRowSelection])

  const handleCheckboxChange = useCallback(() => {
    if (isAllRowsSelected || totalSelectedRows > 0) {
      selectNone()
    } else {
      selectAll()
    }
  }, [isAllRowsSelected, totalSelectedRows, selectNone, selectAll])

  const selectedIds = React.useMemo(
    () => getSelectedThreadIds(threads, rowSelection, isAllRowsSelected),
    [threads, rowSelection, isAllRowsSelected]
  )

  const refreshAfterBulkAction = useCallback(() => {
    selectNone()
    onRefresh()
  }, [selectNone, onRefresh])

  const bulkAssignMutation = useMutation({
    mutationFn: ({
      deal,
      threadIds,
    }: {
      deal: { id: number }
      threadIds: number[]
    }) =>
      api.emailIntegration.assignThreadsToDeal({
        thread_ids: threadIds,
        deal_id: deal.id,
      }),
    onSuccess: (data) => {
      toast.success(`${data.assigned_count} thread(s) assigned to deal`)
      refreshAfterBulkAction()
    },
    onError: () => toast.error('Failed to assign threads to deal'),
  })

  const bulkRemoveMutation = useMutation({
    mutationFn: () => {
      const idsToRemove = selectedIds.filter(
        (id) => threads.find((t) => t.id === id)?.deal_id
      )
      return Promise.allSettled(
        idsToRemove.map((id) => api.emailIntegration.removeThreadFromDeal(id))
      )
    },
    onSuccess: (results) => {
      const succeeded = results.filter((r) => r.status === 'fulfilled').length
      const failed = results.filter((r) => r.status === 'rejected').length
      if (failed === 0) {
        toast.success(`${succeeded} thread(s) removed from deals`)
      } else if (succeeded === 0) {
        toast.error('Failed to remove threads from deals')
      } else {
        toast.warning(`${succeeded} thread(s) removed, ${failed} failed`)
      }
      refreshAfterBulkAction()
    },
  })

  const hasSelection = totalSelectedRows > 0
  const hasAssignedSelected = selectedIds.some(
    (id) => threads.find((t) => t.id === id)?.deal_id
  )

  const start = total === 0 ? 0 : (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)
  const hasPrev = page > 1
  const hasNext = end < total

  return (
    <div className="flex items-center justify-between h-[48px] px-3.5 border-b border-line-subtle bg-surface">
      {/* Left: Checkbox dropdown + Refresh + Actions */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          {/* Bulk select checkbox + dropdown */}
          <div className="flex items-center gap-3 h-8 ml-1">
            <Checkbox
              ref={checkboxRef}
              checked={isAllRowsSelected}
              onChange={handleCheckboxChange}
              onClick={(e) => e.stopPropagation()}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center justify-center size-4 text-fg-secondary hover:text-fg transition-colors"
                >
                  <PiCaretDown className="size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                style={{ minWidth: 160, zIndex }}
                className="bg-surface border-line-focus"
              >
                <DropdownMenuCheckboxItem
                  checked={isAllRowsSelected}
                  onSelect={selectAll}
                >
                  All ({total.toLocaleString()})
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={
                    !isAllRowsSelected &&
                    totalSelectedRows > 0 &&
                    totalSelectedRows === totalRowsInPage
                  }
                  onSelect={selectAllOnPage}
                >
                  All on page ({threads.length})
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={totalSelectedRows === 0}
                  onSelect={selectNone}
                >
                  None
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Refresh */}
          <button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            className="flex items-center justify-center size-8 rounded hover:bg-surface-alt active:bg-surface-alt text-fg-secondary hover:text-fg transition-colors disabled:text-fg-muted disabled:cursor-not-allowed"
            title="Refresh"
          >
            <PiArrowCounterClockwise className="size-[22px]" />
          </button>
        </div>

        {/* Actions dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1.5 h-8 px-1.5 rounded hover:bg-surface-alt active:bg-surface-alt text-sm font-medium text-fg-secondary hover:text-fg transition-colors"
            >
              <PiToolbox className="size-6" />
              <span>Actions</span>
              <PiCaretDown className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            style={{ minWidth: 200, zIndex }}
            className="bg-surface border-line-focus"
          >
            {hasSelection ? (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="flex items-center gap-2 cursor-pointer">
                  <PiPath className="size-4 flex-shrink-0 text-fg-secondary" />
                  <span>Assign all to deal</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="p-0 bg-surface border-line-focus">
                  <DealSearchSubContent
                    contactCompanyId={contactCompanyId}
                    onAssign={(deal) =>
                      bulkAssignMutation.mutate({
                        deal,
                        threadIds: selectedIds,
                      })
                    }
                  />
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            ) : (
              <DropdownMenuItem
                disabled
                className="flex items-center gap-2 opacity-50 cursor-not-allowed"
              >
                <PiPath className="size-4 flex-shrink-0 text-fg-secondary" />
                <span>Assign all to deal</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!hasSelection || !hasAssignedSelected}
              onSelect={() => bulkRemoveMutation.mutate()}
              className={cn(
                'flex items-center gap-2',
                !hasSelection || !hasAssignedSelected
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer'
              )}
            >
              <PiTrash className="size-4 flex-shrink-0 text-fg-secondary" />
              <span>Remove all from deal</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Right: Pagination */}
      <div className="flex items-center gap-[15px] h-[48px] text-sm text-fg-secondary">
        <span className="py-2">
          {total > 0 ? `${start}\u2013${end}` : '0'} of {total.toLocaleString()}
        </span>
        <button
          type="button"
          disabled={!hasPrev}
          onClick={() => onPageChange(page - 1)}
          className={cn(
            'flex items-center justify-center size-[38px] rounded-full transition-colors',
            hasPrev
              ? 'hover:bg-black/5 text-fg-secondary hover:text-fg'
              : 'text-fg-muted cursor-not-allowed'
          )}
        >
          <PiCaretLeft className="size-[22px]" />
        </button>
        <button
          type="button"
          disabled={!hasNext}
          onClick={() => onPageChange(page + 1)}
          className={cn(
            'flex items-center justify-center size-[38px] rounded-full transition-colors',
            hasNext
              ? 'hover:bg-black/5 text-fg-secondary hover:text-fg'
              : 'text-fg-muted cursor-not-allowed'
          )}
        >
          <PiCaretRight className="size-[22px]" />
        </button>
      </div>
    </div>
  )
}
